if (!customElements.get('product-add-on-modal')) {
  customElements.define(
    'product-add-on-modal',
    class ProductAddonsModal extends ModalWindow {
      constructor() {
        super();

        this.swiperInstance = null;
        this.swiperInitialized = false;
        this._boundKeydownHandler = this._handleKeydown.bind(this);
        this.attachAddOnModalListeners();

        // Add click-outside-content-to-close functionality
        this.addEventListener('click', (event) => {
          // Check if click is outside both swiper content and pagination
          const content = this.querySelector('.product-add-on-modal-content .swiper-wrapper');
          const pagination = this.querySelector('.swiper-pagination-ProductAddOnModal');

          if (content && !content.contains(event.target) && (!pagination || !pagination.contains(event.target))) {
            this.hide();
          }
        });
      }

      /**
       * Handles keydown events for Escape to close and arrow keys to navigate slides.
       * @param {KeyboardEvent} event
       */
      _handleKeydown(event) {
        if (event.key === 'Escape') {
          event.preventDefault();
          this.hide();
        } else if (event.key === 'ArrowLeft' && this.swiperInstance) {
          this.swiperInstance.slidePrev();
        } else if (event.key === 'ArrowRight' && this.swiperInstance) {
          this.swiperInstance.slideNext();
        }
      }

      /**
       * Announces a message to screen readers via the aria-live region.
       * @param {string} message - Text to announce
       */
      _announce(message) {
        const liveRegion = this.querySelector('[data-add-on-modal-live-region]');
        if (liveRegion) {
          liveRegion.textContent = '';
          requestAnimationFrame(() => {
            liveRegion.textContent = message;
          });
        }
      }

      /**
       * Initialize Swiper - called when modal is first shown
       */
      initSwiper() {
        if (this.swiperInitialized) {
          // Update swiper and recalculate heights when reopening
          this.swiperInstance?.update();
          // Use requestAnimationFrame to ensure modal is fully visible and rendered
          requestAnimationFrame(() => {
            this.equalizeSlideHeights();
          });
          return;
        }

        const swiperContainer = this.querySelector('.product-add-on-modal-swiper');
        if (!swiperContainer) return;

        const paginationEl = this.querySelector('.swiper-pagination-ProductAddOnModal');
        if (!paginationEl) return;

        // Store reference to this for use in callbacks
        const self = this;

        this.swiperInstance = new window.Swiper(swiperContainer, {
          slidesPerView: 'auto',
          centeredSlides: true,
          spaceBetween: 30,
          pagination: {
            el: paginationEl,
            clickable: true,
          },
          breakpoints: {
            750: {
              spaceBetween: 50,
            },
            1024: {
              spaceBetween: 60,
            },
          },
          on: {
            init: function () {
              self.equalizeSlideHeights();
            },
            resize: function () {
              self.equalizeSlideHeights();
            },
          },
        });

        this.swiperInitialized = true;
      }

      /**
       * Function to equalize slide heights
       * Optimized to use a single loop for better performance
       */
      equalizeSlideHeights() {
        if (!this.swiperInstance) return;

        const slides = this.swiperInstance.slides;
        if (!slides || slides.length === 0) return;

        // Reset heights and find max height in a single pass
        let maxHeight = 0;
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          slide.style.height = 'auto';
          const height = slide.offsetHeight;
          if (height > maxHeight) {
            maxHeight = height;
          }
        }

        // Set all slides to the max height
        if (maxHeight > 0) {
          for (let i = 0; i < slides.length; i++) {
            slides[i].style.height = `${maxHeight}px`;
          }
        }
      }

      attachAddOnModalListeners() {
        // Attach close button listeners to all close buttons within the modal
        const closeButtons = this.querySelectorAll('[data-close-modal]');
        for (const closeButton of closeButtons) {
          closeButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.hide();
          });
        }

        // Attach Add/Remove button listeners
        const addButtons = this.querySelectorAll('[data-add-on-modal-add]');
        for (const addButton of addButtons) {
          addButton.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.handleAddRemoveClick(addButton);
          });
        }
      }

      /**
       * Finds the add-on item in the active (non-inert, non-hidden) set.
       * Falls back to any matching element if no active set is found.
       * @param {string} itemId - The add-on item ID
       * @returns {HTMLElement|null}
       */
      _findActiveAddOnItem(itemId) {
        return (
          document.querySelector(
            `[data-add-on-set]:not([inert]):not(.hidden) [data-add-on-item-id="${itemId}"]`
          ) || document.querySelector(`[data-add-on-item-id="${itemId}"]`)
        );
      }

      /**
       * Handles the Add/Remove button click by triggering the corresponding checkbox
       * @param {HTMLElement} button - The Add/Remove button element
       */
      handleAddRemoveClick(button) {
        const itemId = button.getAttribute('data-add-on-modal-item-id');
        if (!itemId) return;

        // Find the corresponding add-on item in the active (non-inert) set
        const addOnItem = this._findActiveAddOnItem(itemId);
        if (!addOnItem) return;

        // Find the modal item to get the variant ID
        const modalItem = button.closest('[data-add-on-modal-item]');
        if (!modalItem) return;

        // Find the visible modal option to get the variant ID
        const modalOption = modalItem.querySelector('[data-add-on-variant-id]:not(.hidden)');
        if (!modalOption) return;

        const variantId = modalOption.getAttribute('data-add-on-variant-id');

        // Find the corresponding checkbox label in the main add-on section
        const checkboxLabel = addOnItem.querySelector(`[data-add-on-variant-id="${variantId}"]`);
        if (!checkboxLabel) return;

        // Trigger a click on the label to go through the proper state management
        checkboxLabel.click();

        // Sync ALL modal buttons from actual checkbox states after state settles
        setTimeout(() => {
          this.syncButtonsFromCheckboxes();

          // Announce state change to screen readers based on actual state
          const checkbox = checkboxLabel.querySelector('input[type="checkbox"]');
          const isNowAdded = checkbox?.checked || false;
          const title = modalOption?.getAttribute('data-add-on-title') || '';
          this._announce(`${title} ${isNowAdded ? 'added' : 'removed'}`);
        }, 50);
      }

      /**
       * Updates the button visual state.
       * Styling is handled entirely by CSS via [data-button-state] selectors.
       * @param {HTMLElement} button - The button element
       * @param {boolean} isAdded - Whether the item is added
       */
      updateButtonVisualState(button, isAdded) {
        const addText = button.querySelector('[data-add-text]');
        const removeText = button.querySelector('[data-remove-text]');

        button.setAttribute('data-button-state', isAdded ? 'added' : 'initial');
        addText?.classList.toggle('hidden', isAdded);
        removeText?.classList.toggle('hidden', !isAdded);
      }

      /**
       * Syncs the modal button states from the current checkbox states
       * Called when modal is opened to restore state
       */
      syncButtonsFromCheckboxes() {
        const addButtons = this.querySelectorAll('[data-add-on-modal-add]');

        for (const button of addButtons) {
          const itemId = button.getAttribute('data-add-on-modal-item-id');
          if (!itemId) continue;

          // Find the add-on item in the active (non-inert) set
          const addOnItem = this._findActiveAddOnItem(itemId);
          if (!addOnItem) continue;

          // Find the modal item to get the variant ID
          const modalItem = button.closest('[data-add-on-modal-item]');
          if (!modalItem) continue;

          // Find the visible modal option
          const modalOption = modalItem.querySelector('[data-add-on-variant-id]:not(.hidden)');
          if (!modalOption) continue;

          const variantId = modalOption.getAttribute('data-add-on-variant-id');

          // Find the corresponding checkbox
          const checkboxLabel = addOnItem.querySelector(`[data-add-on-variant-id="${variantId}"]`);
          const checkbox = checkboxLabel?.querySelector('input[type="checkbox"]');

          // Update button state based on checkbox state
          this.updateButtonVisualState(button, checkbox?.checked || false);
        }
      }

      show(opener) {
        this.opener = opener;
        window.pauseAllMedia?.();
        this.style.display = 'block';
        this.classList.add('modal-show');

        // Sync button states from checkboxes when modal opens
        this.syncButtonsFromCheckboxes();

        lazyLoadInstance?.update();

        setTimeout(() => {
          document.body.style.overflow = 'hidden';
          this.classList.add('modal-visuallyshow');
        }, 300);

        // Attach keyboard listener for Escape and arrow key navigation
        document.addEventListener('keydown', this._boundKeydownHandler);

        // Initialize Swiper when modal is shown so pagination dots are created (fixes empty dots on mobile)
        requestAnimationFrame(() => {
          trapFocus(this);
          this.initSwiper();
        });
      }

      hide() {
        // Remove keyboard listener
        document.removeEventListener('keydown', this._boundKeydownHandler);

        // Return focus safely: verify opener is still in the DOM before restoring
        const focusTarget = this.opener && document.body.contains(this.opener) ? this.opener : null;
        removeTrapFocus?.(focusTarget);

        this.style.display = 'none';
        this.classList.remove('modal-visuallyshow');
        this.classList.remove('modal-show');
        document.body.style.overflow = 'auto';

        // Dispatch custom event for modal close
        document.dispatchEvent(new CustomEvent('modal-window-closed'));
      }
    }
  );
}
